/**
 * CostBreakdownPanel — a popover, opened from the footer CostCounter, that
 * itemizes a conversation's spend per (model, provider).
 *
 * Every figure is provider-reported (OpenRouter's ``usage.cost`` / settled
 * ``/api/v1/generation`` cost); we never estimate from tokens. Claude-direct
 * rows read "Included" (flat-fee subscription) and any still-settling streaming
 * cost reads "pending" — see the usage ledger (cowork/metrics/usage.py) and
 * docs/design-docs/0006. Shares its shell + helpers with TokenBreakdownPanel
 * (see breakdownShared.tsx).
 *
 * Example row:
 *   Claude Opus 4.8 · OpenRouter   $0.21   ($0.10 in · $0.09 out · $0.02 cached)
 */

import type { CostBreakdownRow } from "@/api/types";
import { ProviderIcon, getProvider } from "@/components/common/ProviderLogos";
import {
  BreakdownPopover,
  formatUsd,
  modelLabel,
  providerDisplayName,
  sourceTagList,
  useBreakdown,
} from "./breakdownShared";

/** The right-hand cost cell, varying by status. */
function CostCell({ row }: { row: CostBreakdownRow }) {
  if (row.cost) {
    const parts = [
      row.cost.input > 0 ? `${formatUsd(row.cost.input)} in` : "",
      row.cost.output > 0 ? `${formatUsd(row.cost.output)} out` : "",
      row.cost.cached > 0 ? `${formatUsd(row.cost.cached)} cached` : "",
    ].filter(Boolean);
    return (
      <span className="text-right tabular-nums">
        <span className="font-semibold text-space">{formatUsd(row.cost.total)}</span>
        {parts.length > 0 && (
          <span className="block text-[10px] text-space-40">{parts.join(" · ")}</span>
        )}
      </span>
    );
  }
  if (row.cost_status === "included")
    return <span className="text-[11px] text-space-40 italic">Included</span>;
  if (row.cost_status === "pending")
    return <span className="text-[11px] text-gold">pending…</span>;
  return <span className="text-[11px] text-space-30">—</span>;
}

export function CostBreakdownPanel({
  conversationId,
  onClose,
}: {
  conversationId: string;
  onClose: () => void;
}) {
  const { data, error } = useBreakdown(conversationId);

  return (
    <BreakdownPopover title="Cost by model" onClose={onClose}>
      {error && <p className="text-[11px] text-space-40">Couldn't load the breakdown.</p>}
      {!error && !data && <p className="text-[11px] text-space-40">Loading…</p>}
      {data && data.rows.length === 0 && (
        <p className="text-[11px] text-space-40">No model spend recorded yet.</p>
      )}

      {data && data.rows.length > 0 && (
        <>
          <ul className="flex flex-col divide-y divide-space-10">
            {data.rows.map((row) => {
              const provider = row.provider || getProvider(row.model);
              const tags = sourceTagList(row.sources);
              return (
                <li
                  key={`${row.model}:${row.provider}`}
                  className="flex items-start justify-between gap-3 py-1.5"
                >
                  <span className="flex items-start gap-1.5 min-w-0">
                    <ProviderIcon model={row.model} className="w-3 h-3 mt-0.5 shrink-0 text-space-40" />
                    <span className="min-w-0">
                      <span className="block text-[11px] font-medium text-space truncate">
                        {modelLabel(row.model)}
                      </span>
                      <span className="block text-[10px] text-space-40">
                        {providerDisplayName(provider)}
                        {tags.length > 0 && ` · ${tags.join(", ")}`}
                      </span>
                    </span>
                  </span>
                  <CostCell row={row} />
                </li>
              );
            })}
          </ul>
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-space-20">
            <span className="text-[11px] font-semibold text-space">Total</span>
            <span className="text-xs font-semibold text-space tabular-nums">
              {formatUsd(data.total_usd)}
            </span>
          </div>
          {data.has_included && (
            <p className="text-[10px] text-space-40 mt-1.5 leading-snug">
              Anthropic-direct usage runs on the Claude subscription and isn't billed per call.
            </p>
          )}
          {data.has_pending && (
            <p className="text-[10px] text-space-40 mt-1 leading-snug">
              Some OpenRouter costs are still settling — reopen in a moment.
            </p>
          )}
        </>
      )}
    </BreakdownPopover>
  );
}
